import { useMutation, useQuery } from '@apollo/client';
import { Empty, message } from 'antd';
import {
  CasesIdentityVerificationV1CaseDetailsContainerIdentityVerificationCaseV1Document,
  CasesIdentityVerificationV1CaseDetailsContainerIdentityVerificationCaseV1UpdateDocument,
  CasesIdentityVerificationV1CaseDetailsContainerIdentityVerificationCaseV1UpdateMutationVariables,
  CasesIdentityVerificationV1CaseDetailsContainerStaffUserCurrentDocument,
  CasesIdentityVerificationV1CaseDetailsContainerStaffUsersDocument,
  StaffUser
} from '../../../../../../generated';
import { useOid } from '../../../../../../hooks/useOid';
import { CaseDetails } from '../../../../../shared/case-details/case-details';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled } from '../../../../../shared/utils/queryOptions';

interface CaseDetailsContainerProps {
  caseId: string;
}

export const CaseDetailsContainer: React.FC<CaseDetailsContainerProps> = (props) => {
  // Query to get the staff data
  const { data: staffData, loading: staffLoading, error: staffError } = useQuery(CasesIdentityVerificationV1CaseDetailsContainerStaffUsersDocument);
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled(oid);

  const {
    data: currentStaffData,
    loading: currentStaffLoading,
    error: currentStaffError
  } = useQuery(CasesIdentityVerificationV1CaseDetailsContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const {
    data: caseData,
    loading: caseLoading,
    error: caseError
  } = useQuery(CasesIdentityVerificationV1CaseDetailsContainerIdentityVerificationCaseV1Document, {
    variables: {
      identityVerificationCaseV1Id: props.caseId
    }
  });

  const [tagsUpdate] = useMutation(CasesIdentityVerificationV1CaseDetailsContainerIdentityVerificationCaseV1UpdateDocument, {});

  // Mutation to update the applicant's applying to data
  const [caseWorkerUpdate] = useMutation(CasesIdentityVerificationV1CaseDetailsContainerIdentityVerificationCaseV1UpdateDocument);

  const staffUsers =
    staffData?.staffUsers
      ?.filter((item): item is StaffUser => item !== null && item !== undefined)
      .filter((staff) => staff.role?.permissions?.idCasePermissions?.canBeAssignedCases) || [];

  // Function to handle the save action
  const handleSave = async (data: StaffUser | null): Promise<void> => {
    try {
      const idVerificationUpdateVariables: CasesIdentityVerificationV1CaseDetailsContainerIdentityVerificationCaseV1UpdateMutationVariables = {
        input: {
          id: props.caseId,
          caseDetails: {
            applicationReview: {
              caseWorkerAssigned: data?.id || null
            }
          }
        }
      };
      const { data: mutationData } = await caseWorkerUpdate({
        variables: idVerificationUpdateVariables
      });
      if (mutationData?.identityVerificationCaseV1Update.status.success === true) {
        message.success('Assignment saved successfully');
      } else {
        message.error(`Error saving Assignment: ${mutationData?.identityVerificationCaseV1Update?.status.errorMessage}`);
      }
    } catch (error) {
      message.error(`Error saving Assignment: ${JSON.stringify(error)}`);
    }
  };

  const handleTagsChange = async (newTags: string[]): Promise<boolean> => {
    try {
      const identityVerificationUpdateVariables: CasesIdentityVerificationV1CaseDetailsContainerIdentityVerificationCaseV1UpdateMutationVariables = {
        input: {
          id: props.caseId,
          tags: newTags
        }
      };
      const result = await tagsUpdate({
        variables: identityVerificationUpdateVariables
      });
      if (!result.data?.identityVerificationCaseV1Update?.status.success) {
        message.error(`Error removing tags : ${result.data?.identityVerificationCaseV1Update?.status.errorMessage}`);
        return false;
      } else {
        message.success('tags updated successfully');
        return true;
      }
    } catch (error) {
      message.error(`Error saving tags: ${JSON.stringify(error)}`);
    }
    return false;
  };

  return (
    <ComponentQueryLoader
      loading={caseLoading || staffLoading || currentStaffLoading}
      hasData={caseData?.identityVerificationCaseV1 && staffData?.staffUsers}
      error={caseError || staffError || currentStaffError}
      hasDataComponent={
        <CaseDetails
          data={{
            caseData: {
              id: caseData?.identityVerificationCaseV1?.id || '',
              state: caseData?.identityVerificationCaseV1?.state || '',
              requestedAt: caseData?.identityVerificationCaseV1?.revisionRequest?.requestedAt || '',
              revisionSubmittedAt: caseData?.identityVerificationCaseV1?.revisionRequest?.revisionSubmittedAt || '',
              tags: caseData?.identityVerificationCaseV1?.tags || []
            }
          }}
          staffInfo={staffUsers}
          currentStaff={currentStaffData?.staffUserCurrent as StaffUser | undefined}
          assignedWorkerInfo={caseData?.identityVerificationCaseV1?.caseDetails?.applicationReview?.caseWorkerAssigned as StaffUser}
          onSave={handleSave}
          onTagsChange={handleTagsChange}
          isCurrentUser={caseData?.identityVerificationCaseV1?.isCurrentUserAssigned ?? false}
        />
      }
      noDataComponent={<Empty description="No data available" />}
    />
  );
};
